import { motion } from "framer-motion";
import { Zap, Wind, Radar, Cpu } from "lucide-react";
import { useLanguage } from "@/i18n";
import OvenBlueprint from "@/components/OvenBlueprint";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

const Technology = () => {
  const { t } = useLanguage();
  const features = [
    { icon: Zap, title: t.tech.f1t, desc: t.tech.f1d, accent: "text-thermal", tid: "tech-feature-microwave" },
    { icon: Wind, title: t.tech.f2t, desc: t.tech.f2d, accent: "text-micro", tid: "tech-feature-air" },
    { icon: Radar, title: t.tech.f3t, desc: t.tech.f3d, accent: "text-micro", tid: "tech-feature-sensors" },
    { icon: Cpu, title: t.tech.f4t, desc: t.tech.f4d, accent: "text-thermal", tid: "tech-feature-control" },
  ];

  return (
    <section id="tecnologia" className="border-b border-white/10" data-testid="technology-section">
      <div className="px-5 md:px-10 py-24 md:py-36">
        <motion.p {...reveal} className="font-mono text-[10px] md:text-xs tracking-[0.35em] text-thermal mb-6" data-testid="tech-label">
          {t.tech.label}
        </motion.p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          <div>
            <motion.h2 {...reveal} className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mb-8" data-testid="tech-title">
              {t.tech.title}
              <br />
              <span className="text-outline-orange">{t.tech.titleAccent}</span>
            </motion.h2>
            <motion.p {...reveal} transition={{ ...reveal.transition, delay: 0.1 }} className="text-sm md:text-base text-white/60 leading-relaxed max-w-lg mb-14" data-testid="tech-body">
              {t.tech.body}
            </motion.p>

            <div className="grid grid-cols-1 sm:grid-cols-2 border-t border-l border-white/10">
              {features.map((f, i) => (
                <motion.div
                  key={f.tid}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  className="border-b border-r border-white/10 p-6 group hover:bg-white/[0.03] transition-colors duration-300"
                  data-testid={f.tid}
                >
                  <f.icon className={`w-5 h-5 mb-4 ${f.accent} transition-transform duration-500 group-hover:scale-110`} strokeWidth={1.5} />
                  <h3 className="font-display font-medium text-lg mb-2">{f.title}</h3>
                  <p className="text-xs md:text-sm text-white/50 leading-relaxed">{f.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:sticky lg:top-28"
          >
            <OvenBlueprint />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Technology;
